import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp, ImagePlus, Mail, Plus, ShieldCheck, Sparkles, Users, X } from "lucide-react";

// API & Utils
import { addGuestsToEvent, getGuestsForUser } from "../api/guests";
import { getEventById } from "../api/events";
import { getToken } from "../api/utils";

// Sub-components
import { TagMediaModal } from "../components/TagMediaModal";
import { GuestForm } from "../components/GuestForm";
import { GuestTable } from "../components/GuestTable";
import { GuestAccessModal } from "../components/GuestAccessModal";
import { MediaSection } from "../components/MediaSection";
import { MediaTagAccessModal } from "../components/MediaTagAccessModal";
import { WorkflowSteps } from "../components/WorkflowSteps";

const API_URL = (process.env.REACT_APP_API_URL || "http://localhost:8000");
const SUBEVENT_LINK_PREFIX = "subevent_link_";
const RSVP_FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "attending", label: "Attending" },
  { id: "not_attending", label: "Not attending" },
  { id: "sent", label: "Invite sent" },
  { id: "not_sent", label: "Not sent" },
];
const ATTENDING_STATUSES = new Set(["attending", "accepted", "yes"]);
const NOT_ATTENDING_STATUSES = new Set(["not_attending", "declined", "no"]);

const normalizeRsvpStatus = (status) => String(status || "pending").trim().toLowerCase();

const GUEST_IMPORT_COLUMNS = ["guest_name", "email_id", "mobile_number", "group_tags", "personal_message"];
const GUEST_EXPORT_COLUMNS = [
  ...GUEST_IMPORT_COLUMNS,
  "invite_sent",
  "rsvp_status",
  "rsvp_adult_count",
  "rsvp_kids_count",
  "rsvp_allergies",
  "rsvp_message",
];

const normalizeColumnName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const csvEscape = (value) => {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"' && inQuotes && nextChar === '"') {
      cell += '"';
      i += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      row.push(cell);
      cell = "";
    } else if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && nextChar === "\n") i += 1;
      row.push(cell);
      if (row.some((value) => value.trim())) rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }

  row.push(cell);
  if (row.some((value) => value.trim())) rows.push(row);
  return rows;
};

const parseGuestCsv = (text) => {
  const rows = parseCsv(text);
  if (rows.length < 2) return [];

  const headers = rows[0].map(normalizeColumnName);
  return rows.slice(1).map((row) => {
    const record = {};
    headers.forEach((header, index) => {
      record[header] = row[index]?.trim() || "";
    });
    return record;
  });
};

const splitTagText = (value) =>
  String(value || "")
    .split(/[;|]/)
    .map((tag) => tag.trim())
    .filter(Boolean);

const matchesRsvpFilter = (guest, filterId) => {
  const status = normalizeRsvpStatus(guest?.rsvp_status);
  if (filterId === "all") return true;
  if (filterId === "pending") return !ATTENDING_STATUSES.has(status) && !NOT_ATTENDING_STATUSES.has(status);
  if (filterId === "attending") return ATTENDING_STATUSES.has(status);
  if (filterId === "not_attending") return NOT_ATTENDING_STATUSES.has(status);
  if (filterId === "sent") return Boolean(guest?.invite_sent);
  if (filterId === "not_sent") return !guest?.invite_sent;
  return true;
};

const extractLinkedSubevents = (metadataArray) =>
  (metadataArray || []).reduce((acc, item) => {
    if (item?.meta_key?.startsWith(SUBEVENT_LINK_PREFIX)) {
      const subeventId = item.meta_key.replace(SUBEVENT_LINK_PREFIX, "");
      acc[subeventId] = item.meta_value === "previous";
    }
    return acc;
  }, {});

const buildDistinctAccessSubevents = (subevents, metadataArray) => {
  const linkedSubevents = extractLinkedSubevents(metadataArray);

  return (subevents || []).reduce((groups, subevent) => {
    const subeventId = String(subevent.id);
    const isLinkedToPrevious = Boolean(linkedSubevents[subeventId]);

    if (isLinkedToPrevious && groups.length > 0) {
      const previousGroup = groups[groups.length - 1];
      previousGroup.access_ids = [...previousGroup.access_ids, subevent.id];
      previousGroup.linked_page_count = (previousGroup.linked_page_count || 0) + 1;
      return groups;
    }

    groups.push({
      ...subevent,
      access_ids: [subevent.id],
      linked_page_count: 0,
    });
    return groups;
  }, []);
};

export const GuestManagerPage = () => {
  // --- STATE MANAGEMENT ---
  const [guests, setGuests] = useState([]);
  const [subevents, setSubevents] = useState([]);
  const [eventMetadata, setEventMetadata] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isMediaLoading, setIsMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState("");
  const personalizationRef = useRef(null);
  const guestImportInputRef = useRef(null);

  // UI State
  const [isMediaPublic, setIsMediaPublic] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTagFilter, setSelectedTagFilter] = useState("All");
  const [selectedRsvpFilter, setSelectedRsvpFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSendingInvites, setIsSendingInvites] = useState(false);
  const [isResendingInvites, setIsResendingInvites] = useState(false);
  const [isPersonalizationOpen, setIsPersonalizationOpen] = useState(false);
  const [isGuestFormOpen, setIsGuestFormOpen] = useState(false);
  
  // Selection/Editing State
  const [guestToEdit, setGuestToEdit] = useState(null); // For Fine-Tune Modal
  const [editingGuestId, setEditingGuestId] = useState(null); // For Main Form
  const [modifiedGuests, setModifiedGuests] = useState(new Set());
  const [activeTag, setActiveTag] = useState(null);
  const [selectedMediaForTags, setSelectedMediaForTags] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [guestImportStatus, setGuestImportStatus] = useState("");

  const [newGuest, setNewGuest] = useState({
    guest_name: "",
    email_id: "",
    mobile_number: "",
    personal_message: "",
    group_tags: [],
    invited_subevent_ids: [],
    media_ids: [],
  });
  
  const [newMediaName, setNewMediaName] = useState("");
  const [newMediaFile, setNewMediaFile] = useState(null);

  const location = useLocation();
  const eventId = location.state?.eventId;

  // --- DATA FETCHING ---
  const fetchData = useCallback(async () => {
    if (!eventId) return;
    setIsMediaLoading(true);
    setMediaError("");
    try {
      const eventData = await getEventById(eventId);
      setSubevents(eventData?.subevents || []);
      setEventMetadata(eventData?.eventmetadata || []);
      setMediaFiles(eventData?.media || []);
    } catch (err) {
      setMediaError("Could not load media right now.");
      console.error("Error fetching data:", err);
    } finally {
      setIsMediaLoading(false);
    }

    try {
      const existingGuests = await getGuestsForUser(eventId);
      if (!existingGuests?.detail) setGuests(existingGuests || []);
    } catch (err) {
      console.error("Error loading guests:", err);
    }
  }, [eventId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // --- DERIVED STATE ---
  const filteredGuests = useMemo(() => {
    return guests.filter(g => {
      const matchesSearch = g.guest.guest_name.toLowerCase().includes(searchTerm.toLowerCase());
      const guestTags = g.group_tags || [];
      const matchesTag = selectedTagFilter === "All" || 
        guestTags.some(tagObj => tagObj.name === selectedTagFilter);
      const matchesRsvp = matchesRsvpFilter(g, selectedRsvpFilter);
      return matchesSearch && matchesTag && matchesRsvp;
    });
  }, [guests, searchTerm, selectedTagFilter, selectedRsvpFilter]);

  // --- EVENT HANDLERS ---
  const markGuestModified = useCallback((guestId) => {
    setModifiedGuests((prev) => new Set(prev).add(guestId));
  }, []);

  const buildGuestPayload = () => {
    const pendingTag = tagInput.trim().replace(",", "");
    const tags = [...(newGuest.group_tags || [])];
    const hasPendingTag = pendingTag
      ? tags.some((tag) => {
          const tagName = typeof tag === "string" ? tag : tag?.name;
          return tagName?.toLowerCase() === pendingTag.toLowerCase();
        })
      : true;

    if (pendingTag && !hasPendingTag) {
      tags.push(pendingTag);
    }

    return {
      ...newGuest,
      group_tags: tags
        .map((tag) => (typeof tag === "string" ? tag : tag?.name))
        .filter(Boolean),
    };
  };

  const handleFormSubmit = async () => {
    const guestPayload = buildGuestPayload();

    if (isEditing) {
      try {
        const response = await fetch(`${API_URL}/guests/${eventId}/guests/${editingGuestId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify(guestPayload),
        });
        if (!response.ok) throw new Error("Update failed");
        await fetchData();
        resetForm();
        setIsGuestFormOpen(false);
      } catch (err) { console.error("Update failed", err); }
    } else {
      if (!guestPayload.guest_name || !guestPayload.email_id) return alert("Name and email are required");
      try {
        await addGuestsToEvent(eventId, guestPayload);
        await fetchData();
        resetForm();
        setIsGuestFormOpen(false);
      } catch (err) { console.error("Error adding guest:", err); }
    }
  };

  const handleAddGuestClick = () => {
    resetForm();
    setIsGuestFormOpen(true);
  };

  const handlePersonalizeClick = () => {
    setIsPersonalizationOpen(true);
    window.setTimeout(() => {
      personalizationRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
  };

  const handleEditClick = (g) => {
    setIsEditing(true);
    setEditingGuestId(g.id);
    setNewGuest({
      guest_name: g.guest.guest_name,
      email_id: g.guest.email_id,
      mobile_number: g.guest.mobile_number || "",
      personal_message: g.personal_message || "",
      // group_tags: [...(g.group_tags || [])],
      group_tags: g.group_tags ? [...g.group_tags] : [],
      invited_subevent_ids: g.subevent_links?.map(l => l.subevent_id) || [],
      media_ids: g.media_permissions?.map(p => p.media?.id) || [],
    });
    setIsGuestFormOpen(true);
  };
  
  const resetForm = () => {
    setIsEditing(false);
    setEditingGuestId(null);
    setTagInput("");
    setNewGuest({
      guest_name: "", email_id: "", mobile_number: "", 
      personal_message: "", group_tags: [], 
      invited_subevent_ids: [], media_ids: []
    });
  };

  const handleCloseGuestForm = () => {
    resetForm();
    setIsGuestFormOpen(false);
  };

  const handleDeleteGuest = async (eventGuestId) => {
    if (!window.confirm("Are you sure you want to remove this guest?")) return;
    try {
      const response = await fetch(`${API_URL}/guests/${eventId}/guests/${eventGuestId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || payload?.message || "Unable to delete guest.");
      }
      setGuests(prevGuests => prevGuests.filter(g => g.id !== eventGuestId));
    } catch (err) {
      console.error("Delete error:", err);
      alert(err?.message || "Unable to delete guest.");
    }
  };

  const handleDownloadGuestTemplate = () => {
    const csv = `${GUEST_IMPORT_COLUMNS.join(",")}\n${[
      "Asha Sharma",
      "asha@example.com",
      "9876543210",
      "Family; Bride Side",
      "Please join us for all ceremonies.",
    ]
      .map(csvEscape)
      .join(",")}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "guest-import-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportGuests = () => {
    const rows = guests.map((guest) => {
      const tags = (guest.group_tags || [])
        .map((tag) => (typeof tag === "string" ? tag : tag?.name))
        .filter(Boolean)
        .join("; ");
      const rsvpStatus = normalizeRsvpStatus(guest.rsvp_status);
      const hasResponded =
        ATTENDING_STATUSES.has(rsvpStatus) || NOT_ATTENDING_STATUSES.has(rsvpStatus);

      return [
        guest.guest?.guest_name,
        guest.guest?.email_id,
        guest.guest?.mobile_number,
        tags,
        guest.personal_message,
        guest.invite_sent ? "yes" : "no",
        guest.rsvp_status || "pending",
        hasResponded ? guest.rsvp_adult_count ?? "" : "",
        hasResponded ? guest.rsvp_kids_count ?? "" : "",
        hasResponded ? guest.rsvp_allergies || "" : "",
        hasResponded ? guest.rsvp_message || "" : "",
      ].map(csvEscape);
    });

    const csv = [GUEST_EXPORT_COLUMNS.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "event-guests.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportGuests = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !eventId) return;

    try {
      setGuestImportStatus("Reading guest file...");
      const records = parseGuestCsv(await file.text());
      const existingEmails = new Set(
        guests.map((guest) => String(guest.guest?.email_id || "").trim().toLowerCase()).filter(Boolean)
      );

      const validRecords = records
        .map((record) => ({
          guest_name: record.guest_name || record.name || record.guest,
          email_id: record.email_id || record.email || record.email_address,
          mobile_number: record.mobile_number || record.phone || record.mobile || "",
          personal_message: record.personal_message || record.message || "",
          group_tags: splitTagText(record.group_tags || record.tags || record.group || ""),
        }))
        .filter((record) => record.guest_name && record.email_id && record.mobile_number);

      const uniqueRecords = validRecords.filter((record) => {
        const email = record.email_id.trim().toLowerCase();
        if (existingEmails.has(email)) return false;
        existingEmails.add(email);
        return true;
      });

      if (uniqueRecords.length === 0) {
        setGuestImportStatus("No new guests found. Check required columns or duplicate emails.");
        return;
      }

      let importedCount = 0;
      for (const record of uniqueRecords) {
        const response = await addGuestsToEvent(eventId, record);
        if (response?.detail || response?.message === "error") {
          throw new Error(response.detail || "Unable to import one or more guests.");
        }
        importedCount += 1;
      }

      await fetchData();
      setGuestImportStatus(`Imported ${importedCount} guest${importedCount === 1 ? "" : "s"}.`);
    } catch (error) {
      console.error("Guest import failed:", error);
      setGuestImportStatus(error?.message || "Unable to import guests.");
    }
  };

  const handleSaveChanges = async () => {
    if (modifiedGuests.size === 0) return alert("No changes to save.");
    try {
      const payload = guests
        .filter((g) => modifiedGuests.has(g.guest.id))
        .map((g) => ({
          guest_id: g.guest.id,
          subevent_ids: g.subevent_links?.map((link) => link.subevent_id) || [],
          media_ids: g.media_permissions?.map((perm) => perm.media?.id) || [],
        }));

      const res = await fetch(`${API_URL}/events/${eventId}/guests/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        // alert("Updates saved successfully!");
        setModifiedGuests(new Set());
        setIsModalOpen(false);
      }
    } catch (err) { alert("Error saving updates"); }
  };

  // --- PERMISSION TOGGLES (FOR MODAL) ---
  const toggleInvite = (subeventIds, shouldInvite) => {
    if (!guestToEdit) return;
    const guestId = guestToEdit.guest.id;
    const ids = Array.isArray(subeventIds) ? subeventIds : [subeventIds];
    const idKeys = new Set(ids.map((id) => String(id)));

    setGuests(prev => prev.map(g => {
      if (g.guest.id !== guestId) return g;
      const currentLinks = g.subevent_links || [];
      const alreadyInvited = ids.every((id) =>
        currentLinks.some((link) => String(link.subevent_id) === String(id))
      );
      const nextShouldInvite = shouldInvite ?? !alreadyInvited;
      const linksWithoutGroup = currentLinks.filter((link) => !idKeys.has(String(link.subevent_id)));
      const updatedLinks = nextShouldInvite
        ? [
            ...linksWithoutGroup,
            ...ids.map((id) => ({ subevent_id: id, rsvp_status: "pending" })),
          ]
        : linksWithoutGroup;
      const updated = { ...g, subevent_links: updatedLinks };
      setGuestToEdit(updated);
      return updated;
    }));
    markGuestModified(guestId);
  };

  const toggleMedia = (mediaId) => {
    if (!guestToEdit) return;
    const guestId = guestToEdit.guest.id;
    setGuests(prev => prev.map(g => {
      if (g.guest.id !== guestId) return g;
      const currentPermissions = g.media_permissions || [];
      const hasMedia = currentPermissions.some(p => p.media?.id === mediaId);
      const updatedPermissions = hasMedia 
        ? currentPermissions.filter(p => p.media?.id !== mediaId)
        : [...currentPermissions, { media: { id: mediaId } }];
      const updated = { ...g, media_permissions: updatedPermissions };
      setGuestToEdit(updated);
      return updated;
    }));
    markGuestModified(guestId);
  };

  const handleUploadMedia = async () => {
    if (!newMediaFile || !newMediaName.trim()) {
      throw new Error("Please provide a media name and file.");
    }

    const formData = new FormData();
    formData.append("file", newMediaFile);
    formData.append("name", newMediaName.trim());
    formData.append("media_type", isMediaPublic ? "user_public" : "user_private");

    const res = await fetch(`${API_URL}/media/events/${eventId}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}` },
      body: formData,
    });
    if (!res.ok) throw new Error("Failed to upload media.");

    const media = await res.json();
    setMediaFiles((prev) => [...prev, media]);
    setNewMediaFile(null);
    setNewMediaName("");
    return media;
  };

  const handleToggleMediaAccess = async (media) => {
    const isCurrentlyPublic = media.is_public || media.media_type === "user_public";
    const nextMediaType = isCurrentlyPublic ? "user_private" : "user_public";
    const nextIsPublic = !isCurrentlyPublic;

    setMediaFiles((prev) =>
      prev.map((item) =>
        item.id === media.id
          ? { ...item, media_type: nextMediaType, is_public: nextIsPublic }
          : item
      )
    );

    try {
      const response = await fetch(`${API_URL}/media/events/${eventId}/${media.id}/visibility`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ media_type: nextMediaType }),
      });

      if (!response.ok) {
        throw new Error("Failed to update media visibility.");
      }

      const updatedMedia = await response.json().catch(() => null);
      if (updatedMedia?.id) {
        setMediaFiles((prev) =>
          prev.map((item) => (item.id === media.id ? { ...item, ...updatedMedia } : item))
        );
      }
    } catch (err) {
      setMediaFiles((prev) =>
        prev.map((item) =>
          item.id === media.id
            ? { ...item, media_type: media.media_type, is_public: media.is_public }
            : item
        )
      );
      throw err;
    }
  };

  const handleDeleteMedia = async (media) => {
    const response = await fetch(`${API_URL}/media/events/${eventId}/${media.id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${getToken()}` },
    });

    if (!response.ok) {
      throw new Error("Failed to delete media.");
    }

    setMediaFiles((prev) => prev.filter((item) => item.id !== media.id));
    setSelectedMediaForTags((prev) => (prev?.id === media.id ? null : prev));
  };

  const submitInviteJob = async ({ forceResend = false } = {}) => {
    const query = forceResend ? "?force_resend=true" : "";
    const res = await fetch(`${API_URL}/events/${eventId}/invite${query}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}` },
    });

    if (!res.ok) {
      const payload = await res.json().catch(() => null);
      throw new Error(payload?.detail || payload?.message || "Failed to send invites.");
    }

    return await res.json().catch(() => null);
  };

  const handleSendInvites = async () => {
    if (!eventId || isSendingInvites || isResendingInvites) return;
    setIsSendingInvites(true);
    try {
      const payload = await submitInviteJob();
      alert(payload?.job_id ? `Invite job created: ${payload.job_id}` : "Invite job created.");
    } catch (err) {
      alert(err?.message || "Failed to send invites.");
    } finally {
      setIsSendingInvites(false);
    }
  };

  const handleResendInvites = async () => {
    if (!eventId || isSendingInvites || isResendingInvites) return;
    const shouldResend = window.confirm(
      "Resend invites to all guests, including guests who already received one? Use this mainly for testing."
    );
    if (!shouldResend) return;

    setIsResendingInvites(true);
    try {
      const payload = await submitInviteJob({ forceResend: true });
      alert(payload?.job_id ? `Resend invite job created: ${payload.job_id}` : "Resend invite job created.");
    } catch (err) {
      alert(err?.message || "Failed to resend invites.");
    } finally {
      setIsResendingInvites(false);
    }
  };

  const allTags = useMemo(() => {
    const uniqueTags = [{ id: 'all-option', name: 'All' }];
    const seenNames = new Set(["All"]);
  
    guests.forEach(g => {
      (g?.group_tags || []).forEach(tag => {
        if (tag.name && !seenNames.has(tag.name)) {
          seenNames.add(tag.name);
          uniqueTags.push(tag);
        }
      });
    });
  
    return uniqueTags;
  }, [guests]);

  const rsvpFilterOptions = useMemo(
    () =>
      RSVP_FILTERS.map((filter) => ({
        ...filter,
        count: guests.filter((guest) => matchesRsvpFilter(guest, filter.id)).length,
      })),
    [guests]
  );

  const guestStats = useMemo(() => {
    const groupNames = new Set();
    let customAccessCount = 0;
    let inviteSentCount = 0;
    let attendingCount = 0;
    let notAttendingCount = 0;
    let respondedCount = 0;

    guests.forEach((guest) => {
      (guest?.group_tags || []).forEach((tag) => {
        const tagName = typeof tag === "string" ? tag : tag?.name;
        if (tagName) groupNames.add(tagName);
      });

      if ((guest?.subevent_links || []).length > 0 || (guest?.media_permissions || []).length > 0) {
        customAccessCount += 1;
      }

      if (guest?.invite_sent) {
        inviteSentCount += 1;
      }

      const rsvpStatus = String(guest?.rsvp_status || "pending").trim().toLowerCase();
      if (["attending", "accepted", "yes"].includes(rsvpStatus)) {
        attendingCount += 1;
        respondedCount += 1;
      } else if (["not_attending", "declined", "no"].includes(rsvpStatus)) {
        notAttendingCount += 1;
        respondedCount += 1;
      }
    });

    const privateMediaCount = mediaFiles.filter(
      (media) => !(media.media_type === "user_public" || media.is_public)
    ).length;

    return {
      totalGuests: guests.length,
      groupCount: groupNames.size,
      privateMediaCount,
      publicMediaCount: mediaFiles.length - privateMediaCount,
      customAccessCount,
      inviteSentCount,
      attendingCount,
      notAttendingCount,
      respondedCount,
    };
  }, [guests, mediaFiles]);

  const accessSubevents = useMemo(
    () => buildDistinctAccessSubevents(subevents, eventMetadata),
    [subevents, eventMetadata]
  );

  return (
    <div className="min-h-screen bg-[#fff4fb] p-6 md:p-12 font-sans text-gray-800">
      <div className="max-w-7xl mx-auto space-y-10">
        <WorkflowSteps currentStep="guests" />

        <section className="overflow-hidden rounded-[46px] border border-white bg-white shadow-2xl">
          <div className="grid gap-8 p-8 lg:grid-cols-[1.15fr_0.85fr] lg:p-10">
            <div className="space-y-5">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#fff1f8] px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-[#b12d75]">
                <Users size={14} /> Simple invite mode
              </span>
              <div>
                <h1 className="text-4xl font-black tracking-tight text-gray-950 md:text-5xl">
                  Add guests and send when ready.
                </h1>
                <p className="mt-4 max-w-2xl text-sm font-medium leading-7 text-gray-500">
                  Everyone receives the standard invitation experience by default. If you want
                  private media, ceremony-level access, or group-specific personalization, open
                  the optional personalization panel below.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleAddGuestClick}
                  className="inline-flex items-center gap-2 rounded-full bg-[#ff0099] px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-white shadow-lg shadow-[#ff0099]/20 transition hover:bg-gray-950"
                >
                  <Plus size={15} /> Add Guest
                </button>
                <button
                  type="button"
                  onClick={handlePersonalizeClick}
                  className="inline-flex items-center gap-2 rounded-full border border-[#f2cfe5] bg-white px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-[#a52b69] transition hover:border-[#ff0099] hover:text-[#ff0099]"
                >
                  <Sparkles size={15} /> Personalize guest experience
                </button>
                <span className="inline-flex items-center rounded-full bg-gray-950 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-white">
                  {guestStats.totalGuests} guest{guestStats.totalGuests === 1 ? "" : "s"}
                </span>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-[28px] bg-[#fff7fb] p-5">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#b07c98]">Invites sent</p>
                <p className="mt-2 text-3xl font-black text-gray-950">{guestStats.inviteSentCount}</p>
                <p className="mt-1 text-xs font-semibold text-gray-500">Guests who have been emailed.</p>
              </div>
              <div className="rounded-[28px] bg-[#f6f1ff] p-5">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-purple-500">Responded</p>
                <p className="mt-2 text-3xl font-black text-gray-950">{guestStats.respondedCount}</p>
                <p className="mt-1 text-xs font-semibold text-gray-500">{guestStats.attendingCount} attending, {guestStats.notAttendingCount} not attending.</p>
              </div>
              <div className="rounded-[28px] bg-[#f3fff8] p-5">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">Groups</p>
                <p className="mt-2 text-3xl font-black text-gray-950">{guestStats.groupCount}</p>
                <p className="mt-1 text-xs font-semibold text-gray-500">Optional labels like Family or Friends.</p>
              </div>
              <div className="rounded-[28px] bg-[#fff8ed] p-5">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-600">Custom access</p>
                <p className="mt-2 text-3xl font-black text-gray-950">{guestStats.customAccessCount}</p>
                <p className="mt-1 text-xs font-semibold text-gray-500">Guests with private media or ceremony rules.</p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 1: GUEST MANAGEMENT */}
        <GuestTable 
          guests={guests}
          filteredGuests={filteredGuests}
          modifiedGuests={modifiedGuests}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedTagFilter={selectedTagFilter}
          setSelectedTagFilter={setSelectedTagFilter}
          selectedRsvpFilter={selectedRsvpFilter}
          setSelectedRsvpFilter={setSelectedRsvpFilter}
          rsvpFilters={rsvpFilterOptions}
          allTags={allTags}
          onAddGuest={handleAddGuestClick}
          onExportGuests={handleExportGuests}
          onImportGuests={() => guestImportInputRef.current?.click()}
          onDownloadGuestTemplate={handleDownloadGuestTemplate}
          guestImportStatus={guestImportStatus}
          onEdit={handleEditClick}
          onDelete={handleDeleteGuest}
          onTagClick={(tag) => { setActiveTag(tag); setIsTagModalOpen(true); }}
          onFineTune={(g) => { setGuestToEdit(g); setIsModalOpen(true); }}
        />
        <input
          ref={guestImportInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handleImportGuests}
        />

        {/* SECTION 2: OPTIONAL PERSONALIZATION */}
        <section ref={personalizationRef} className="scroll-mt-6 overflow-hidden rounded-[46px] border border-white bg-white shadow-2xl">
          <button
            type="button"
            onClick={() => setIsPersonalizationOpen((value) => !value)}
            className="flex w-full flex-col gap-4 p-7 text-left transition hover:bg-[#fff9fd] md:flex-row md:items-center md:justify-between md:p-8"
            aria-expanded={isPersonalizationOpen}
          >
            <div className="flex gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#fff1f8] text-[#ff0099]">
                <ImagePlus size={22} />
              </span>
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#b07c98]">
                  Optional
                </p>
                <h2 className="mt-1 text-2xl font-black text-gray-950">Personalize guest experience</h2>
                <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-gray-500">
                  Upload extra media chapters, make them public/private, and grant access by
                  guest or group. Skip this if every guest should receive the same invite.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs font-black uppercase tracking-[0.18em] text-gray-500">
              <ShieldCheck size={16} />
              {isPersonalizationOpen ? "Hide options" : "Show options"}
              {isPersonalizationOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>
          </button>

          {isPersonalizationOpen && (
            <div className="border-t border-[#f4e6ef] p-6 md:p-8">
              <MediaSection 
                mediaFiles={mediaFiles}
                isMediaLoading={isMediaLoading}
                mediaError={mediaError}
                newMediaName={newMediaName}
                setNewMediaName={setNewMediaName}
                newMediaFile={newMediaFile}
                setNewMediaFile={setNewMediaFile}
                isMediaPublic={isMediaPublic}
                setIsMediaPublic={setIsMediaPublic}
                onUpload={handleUploadMedia}
                onToggleMediaAccess={handleToggleMediaAccess}
                onDeleteMedia={handleDeleteMedia}
                onAddTagAccess={(file) => setSelectedMediaForTags(file)}
              />
            </div>
          )}
        </section>

        {/* Tag Selection Popover (Grant Tag Access) */}
        {selectedMediaForTags && (
          <MediaTagAccessModal 
            media={selectedMediaForTags}
            allTags={allTags}
            onClose={() => setSelectedMediaForTags(null)}
            onRefresh={fetchData}
          />
        )}

        {/* SECTION 3: SAVE ACTIONS */}
        <section className="bg-white rounded-[50px] p-8 text-center shadow-2xl space-y-5 md:p-10">
           <div>
              <h2 className="text-2xl font-black text-gray-950">Ready to invite?</h2>
              <p className="mt-2 text-sm font-medium text-gray-500">
                You can send now with the standard invite, or personalize guest access first.
              </p>
           </div>
           <div className="flex flex-col md:flex-row justify-center gap-4">
              {/* <button onClick={handleSaveChanges} className="flex items-center gap-3 px-10 py-5 bg-gray-900 text-white rounded-[30px] font-black hover:bg-black transition-all shadow-xl">
                <Save size={20} /> Save All Changes
              </button> */}
              <button
                onClick={handleSendInvites}
                disabled={isSendingInvites || isResendingInvites}
                className="flex items-center gap-3 px-10 py-5 bg-[#ff0099] text-white rounded-[30px] font-black hover:bg-black transition-all shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Mail size={20} /> {isSendingInvites ? "Sending..." : "Send Invites to Guests"}
              </button>
              <button
                onClick={handleResendInvites}
                disabled={isSendingInvites || isResendingInvites}
                className="flex items-center gap-3 px-10 py-5 bg-gray-100 text-gray-700 rounded-[30px] font-black hover:bg-gray-900 hover:text-white transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Mail size={20} /> {isResendingInvites ? "Resending..." : "Resend Invites"}
              </button>
           </div>
        </section>
      </div>

      {/* MODALS */}
      {isGuestFormOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm md:items-stretch md:justify-end">
          <button
            type="button"
            className="absolute inset-0"
            aria-label="Close guest form"
            onClick={handleCloseGuestForm}
          />
          <aside className="relative z-10 max-h-[88vh] w-full overflow-y-auto rounded-t-[30px] bg-white p-4 shadow-2xl md:h-full md:max-h-none md:max-w-md md:rounded-l-[36px] md:rounded-tr-none md:p-6">
            <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-gray-200 md:hidden" />
            <div className="mb-3 flex items-start justify-between gap-4 px-1 md:mb-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#b07c98]">
                  {isEditing ? "Edit Guest" : "Simple Invite"}
                </p>
                <h2 className="mt-1 text-xl font-black text-gray-950 md:text-2xl">
                  {isEditing ? "Update guest details" : "Add a guest"}
                </h2>
              </div>
              <button
                type="button"
                onClick={handleCloseGuestForm}
                className="rounded-full bg-gray-100 p-2.5 text-gray-500 transition hover:bg-gray-950 hover:text-white md:p-3"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <GuestForm 
              newGuest={newGuest}
              setNewGuest={setNewGuest}
              tagInput={tagInput}
              setTagInput={setTagInput}
              isEditing={isEditing}
              onReset={handleCloseGuestForm}
              onSubmit={handleFormSubmit}
              compact
            />
          </aside>
        </div>
      )}

      {isModalOpen && (
        <GuestAccessModal 
            guest={guestToEdit}
            subevents={accessSubevents}
            mediaFiles={mediaFiles}
            onClose={() => setIsModalOpen(false)}
            onToggleInvite={toggleInvite}
            onToggleMedia={toggleMedia}
            OnSave={handleSaveChanges}
        />
      )}

      {isTagModalOpen && (
        <TagMediaModal 
            tag={activeTag} 
            mediaFiles={mediaFiles} 
            onClose={() => setIsTagModalOpen(false)} 
            onRefresh={fetchData} 
        />
      )}
    </div>
  );
};
